import React, { useEffect, useState } from 'react';
import { api, useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Mail, Star, Plus, Trash2, Pencil, Save, PenSquare, Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const VARIABLES = [
  '{enseigne}', '{ville}', '{code_postal}', '{rue}', '{nom_magasin}',
  '{prenom_dirigeant}', '{nom_dirigeant}', '{dirigeant}',
];

const AI_STYLES = [
  { value: 'professionnel-chaleureux', label: 'Professionnel & chaleureux' },
  { value: 'direct-roi', label: 'Direct & chiffré (ROI)' },
  { value: 'court-percutant', label: 'Court & percutant' },
  { value: 'conversation', label: 'Conversationnel' },
];
const AI_LONGUEURS = [
  { value: 'court', label: 'Court (< 80 mots)' },
  { value: 'moyen', label: 'Moyen (150-200)' },
  { value: 'long', label: 'Long (250-350)' },
];

export default function MonEspacePage() {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [settings, setSettings] = useState({
    signature: '', sender_name_override: '', reply_to_override: '', auto_append_signature: true,
  });
  const [loading, setLoading] = useState(true);

  const [editing, setEditing] = useState(null); // template being created/edited
  const [form, setForm] = useState({ name: '', subject: '', body: '', is_default: false });

  // AI suggestion state (inside template editor)
  const [aiOpen, setAiOpen] = useState(false);
  const [aiInstructions, setAiInstructions] = useState('');
  const [aiStyle, setAiStyle] = useState('professionnel-chaleureux');
  const [aiLongueur, setAiLongueur] = useState('moyen');
  const [aiLoading, setAiLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [t, s] = await Promise.all([
        api.get('/users/me/email-templates'),
        api.get('/users/me/email-settings'),
      ]);
      setTemplates(t.data.templates || []);
      setSettings({
        signature: s.data.signature || '',
        sender_name_override: s.data.sender_name_override || '',
        reply_to_override: s.data.reply_to_override || '',
        auto_append_signature: s.data.auto_append_signature !== false,
      });
    } catch (e) {
      toast.error('Erreur chargement');
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const saveSettings = async () => {
    try {
      await api.put('/users/me/email-settings', settings);
      toast.success('Préférences enregistrées');
    } catch { toast.error('Erreur'); }
  };

  const openCreate = () => {
    setEditing({ id: null });
    setForm({ name: '', subject: '', body: '', is_default: false });
    setAiOpen(false);
    setAiInstructions('');
  };
  const openEdit = (tpl) => {
    setEditing(tpl);
    setForm({ name: tpl.name, subject: tpl.subject, body: tpl.body, is_default: !!tpl.is_default });
    setAiOpen(false);
    setAiInstructions('');
  };

  const generateAI = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.post('/users/me/ai-suggest-template', {
        instructions: aiInstructions,
        style: aiStyle,
        longueur: aiLongueur,
      });
      setForm(f => ({
        ...f,
        name: f.name || data.name || 'Template IA',
        subject: data.subject,
        body: data.body,
      }));
      setAiOpen(false);
      toast.success('Exemple IA généré');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur IA');
    }
    setAiLoading(false);
  };
  const submitTemplate = async (e) => {
    e.preventDefault();
    try {
      if (editing && editing.id) {
        await api.put(`/users/me/email-templates/${editing.id}`, form);
        toast.success('Template mis à jour');
      } else {
        await api.post('/users/me/email-templates', form);
        toast.success('Template créé');
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };
  const deleteTemplate = async (id) => {
    if (!window.confirm('Supprimer ce template ?')) return;
    try {
      await api.delete(`/users/me/email-templates/${id}`);
      toast.success('Supprimé');
      load();
    } catch { toast.error('Erreur'); }
  };

  const insertVariable = (v) => {
    setForm(f => ({ ...f, body: (f.body || '') + ' ' + v }));
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="mon-espace-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>Mon espace email</h1>
        <p className="text-sm text-[#71717A] mt-1">Gérez vos templates de prospection et votre signature personnelle.</p>
      </div>

      {/* === SIGNATURE & PREFS === */}
      <Card className="border border-[#E5E7EB] shadow-sm">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center gap-2">
            <PenSquare className="w-4 h-4 text-[#F97316]" />
            <h2 className="text-sm font-semibold">Signature & préférences d'expédition</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm mb-1 block">Nom d'expéditeur affiché</Label>
              <Input value={settings.sender_name_override}
                onChange={e => setSettings(s => ({ ...s, sender_name_override: e.target.value }))}
                placeholder={`${user?.prenom || ''} ${user?.nom || ''}`.trim() || 'Votre nom'}
                className="h-10 bg-white" data-testid="sender-name-input" />
              <p className="text-xs text-[#71717A] mt-1">Par défaut : votre prénom + nom.</p>
            </div>
            <div>
              <Label className="text-sm mb-1 block">Adresse reply-to (optionnel)</Label>
              <Input value={settings.reply_to_override}
                onChange={e => setSettings(s => ({ ...s, reply_to_override: e.target.value }))}
                placeholder={user?.email || 'votre@wasabicorner.fr'}
                className="h-10 bg-white" data-testid="reply-to-input" />
              <p className="text-xs text-[#71717A] mt-1">Laisser vide pour utiliser votre email principal.</p>
            </div>
          </div>
          <div>
            <Label className="text-sm mb-1 block">Signature email</Label>
            <Textarea rows={6} value={settings.signature}
              onChange={e => setSettings(s => ({ ...s, signature: e.target.value }))}
              placeholder={`Cordialement,\n${user?.prenom || ''} ${user?.nom || ''}\nWasabi Corner — 06 12 34 56 78`}
              className="bg-white" data-testid="signature-textarea" />
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={settings.auto_append_signature}
              onCheckedChange={v => setSettings(s => ({ ...s, auto_append_signature: v }))}
              data-testid="auto-append-switch" />
            <Label className="text-sm cursor-pointer">Ajouter automatiquement ma signature à la fin de chaque email</Label>
          </div>
          <div className="flex justify-end">
            <Button onClick={saveSettings} data-testid="save-email-settings-btn"
              className="h-10 gap-2 bg-[#F97316] hover:bg-[#EA580C] text-white">
              <Save className="w-4 h-4" /> Enregistrer
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* === TEMPLATES === */}
      <Card className="border border-[#E5E7EB] shadow-sm">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-[#F97316]" />
              <h2 className="text-sm font-semibold">Mes templates de prospection</h2>
              <Badge variant="secondary" className="text-xs bg-[#F3F4F6]">{templates.length}</Badge>
            </div>
            <Button onClick={openCreate} data-testid="create-template-btn"
              className="h-9 gap-2 bg-[#F97316] hover:bg-[#EA580C] text-white">
              <Plus className="w-4 h-4" /> Nouveau template
            </Button>
          </div>

          {loading ? (
            <p className="text-sm text-[#71717A]">Chargement...</p>
          ) : templates.length === 0 ? (
            <div className="text-center py-12 text-[#71717A] border border-dashed border-[#E5E7EB] rounded-lg">
              <Mail className="w-8 h-8 mx-auto mb-2 text-[#CBD5E1]" />
              <p className="text-sm">Aucun template pour le moment.</p>
              <p className="text-xs mt-1">Créez-en un pour l'utiliser depuis la fiche prospect.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {templates.map(t => (
                <div key={t.id} className="p-4 border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB]" data-testid={`template-${t.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm truncate">{t.name}</p>
                        {t.is_default && <Badge className="bg-[#FFF7ED] text-[#F97316] text-xs border border-[#F97316]/30"><Star className="w-3 h-3 mr-1" />Par défaut</Badge>}
                      </div>
                      <p className="text-xs text-[#71717A] truncate mt-0.5">{t.subject || '(Sans objet)'}</p>
                      <p className="text-xs text-[#A1A1AA] truncate mt-1 line-clamp-2">{t.body}</p>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(t)} data-testid={`edit-template-${t.id}`} className="h-8 w-8 p-0">
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteTemplate(t.id)} data-testid={`delete-template-${t.id}`}
                        className="h-8 w-8 p-0 text-[#EF4444] hover:bg-[#FEF2F2]">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* === TEMPLATE EDITOR DIALOG === */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl" data-testid="template-editor-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>
              {editing?.id ? 'Modifier le template' : 'Nouveau template'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submitTemplate} className="space-y-3 pt-2 max-h-[70vh] overflow-y-auto">
            {/* AI Assistant */}
            <div className="rounded-lg border border-[#F97316]/30 bg-gradient-to-br from-[#FFF7ED] to-white p-3">
              {!aiOpen ? (
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 text-sm">
                    <Sparkles className="w-4 h-4 text-[#F97316]" />
                    <span className="font-medium">Besoin d'inspiration ?</span>
                    <span className="text-xs text-[#71717A]">Laissez l'IA générer un exemple de template.</span>
                  </div>
                  <Button type="button" size="sm" onClick={() => setAiOpen(true)} data-testid="ai-open-btn"
                    className="h-8 gap-1 bg-[#F97316] hover:bg-[#EA580C] text-white text-xs">
                    <Sparkles className="w-3.5 h-3.5" /> Générer avec l'IA
                  </Button>
                </div>
              ) : (
                <div className="space-y-2.5">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <Sparkles className="w-4 h-4 text-[#F97316]" /> Assistant IA
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs mb-1 block text-[#71717A]">Style</Label>
                      <select value={aiStyle} onChange={e => setAiStyle(e.target.value)}
                        className="w-full h-9 text-xs bg-white border border-[#E5E7EB] rounded px-2"
                        data-testid="ai-style-select">
                        {AI_STYLES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs mb-1 block text-[#71717A]">Longueur</Label>
                      <select value={aiLongueur} onChange={e => setAiLongueur(e.target.value)}
                        className="w-full h-9 text-xs bg-white border border-[#E5E7EB] rounded px-2"
                        data-testid="ai-longueur-select">
                        {AI_LONGUEURS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs mb-1 block text-[#71717A]">Instructions (optionnel)</Label>
                    <Textarea rows={2} value={aiInstructions} onChange={e => setAiInstructions(e.target.value)}
                      placeholder="Ex: mentionner notre offre à 0 € d'investissement, viser les magasins > 2500m²"
                      className="bg-white text-xs" data-testid="ai-instructions-input" />
                  </div>
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => setAiOpen(false)}
                      className="flex-1 h-8 text-xs">Annuler</Button>
                    <Button type="button" size="sm" onClick={generateAI} disabled={aiLoading}
                      data-testid="ai-generate-btn"
                      className="flex-1 h-8 gap-1 bg-[#F97316] hover:bg-[#EA580C] text-white text-xs">
                      {aiLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                      {aiLoading ? 'Génération...' : 'Générer un exemple'}
                    </Button>
                  </div>
                  <p className="text-[10px] text-[#A1A1AA] italic">
                    Le subject et le corps seront pré-remplis. Vous pourrez ensuite les modifier librement.
                  </p>
                </div>
              )}
            </div>

            <div>
              <Label className="text-sm mb-1 block">Nom</Label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required
                className="h-10 bg-white" data-testid="template-name-input" />
            </div>
            <div>
              <Label className="text-sm mb-1 block">Objet</Label>
              <Input value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
                className="h-10 bg-white" placeholder="Proposition pour {enseigne} {ville}" data-testid="template-subject-input" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-sm">Corps de l'email</Label>
                <div className="flex flex-wrap gap-1 justify-end">
                  {VARIABLES.map(v => (
                    <button type="button" key={v} onClick={() => insertVariable(v)}
                      className="text-[10px] px-1.5 py-0.5 rounded border border-[#E5E7EB] text-[#71717A] hover:bg-[#F3F4F6]">
                      {v}
                    </button>
                  ))}
                </div>
              </div>
              <Textarea rows={10} value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
                className="bg-white font-mono text-sm" data-testid="template-body-input" />
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.is_default}
                onCheckedChange={v => setForm(f => ({ ...f, is_default: v }))}
                data-testid="template-default-switch" />
              <Label className="text-sm cursor-pointer">Utiliser comme template par défaut</Label>
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setEditing(null)} className="flex-1 h-10">Annuler</Button>
              <Button type="submit" data-testid="submit-template-btn"
                className="flex-1 h-10 bg-[#F97316] hover:bg-[#EA580C] text-white">
                <Save className="w-4 h-4 mr-2" /> {editing?.id ? 'Mettre à jour' : 'Créer'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
